import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, LogOut, Trash2, MessageSquare, Camera } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ProfileDialog } from "./ProfileDialog";
import { FeedbackDialog } from "./FeedbackDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";

interface ProfileData {
  full_name?: string | null;
  avatar_url?: string | null;
}

export function UserMenu() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [openProfile, setOpenProfile] = useState(false);
  const [openFeedback, setOpenFeedback] = useState(false);
  const [openClearConfirm, setOpenClearConfirm] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  useEffect(() => {
    if (!user) return;

    const fetchProfile = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("full_name, avatar_url")
        .eq("id", user.id)
        .maybeSingle();

      if (data) setProfile(data);
    };

    fetchProfile();
  }, [user]);

  const initials = (() => {
    if (profile?.full_name) {
      return profile.full_name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return "?";
  })();

  const handleLogout = async () => {
    await signOut();
    navigate("/auth");
  };

  const handleClearHistory = async () => {
    setIsClearing(true);
    try {
      // Delete all conversations for the user (messages cascade delete)
      const { error } = await supabase
        .from("conversations")
        .delete()
        .eq("user_id", user?.id);

      if (error) throw error;

      toast({
        title: "History cleared",
        description: "All chat conversations have been deleted.",
      });

      // Refresh the page to show empty state
      window.location.reload();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to clear chat history. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsClearing(false);
      setOpenClearConfirm(false);
    }
  };

  const handleProfileSaved = () => {
    // Refresh profile data
    if (user) {
      supabase
        .from("profiles")
        .select("full_name, avatar_url")
        .eq("id", user.id)
        .maybeSingle()
        .then(({ data }) => {
          if (data) setProfile(data);
        });
    }
  };

  return (
    <>
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 rounded-full ring-1 ring-border hover:ring-primary transition-all"
            aria-label="User menu"
          >
            {profile?.avatar_url ? (
              <img
                src={profile.avatar_url}
                alt="User avatar"
                className="h-10 w-10 rounded-full object-cover"
              />
            ) : (
              <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                {initials}
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0" align="end">
          <div className="p-4 space-y-1">
            <p className="text-xs text-muted-foreground">Signed in as</p>
            <p className="text-sm font-medium truncate">
              {profile?.full_name || user?.email}
            </p>
            {profile?.full_name && (
              <p className="text-xs text-muted-foreground truncate">
                {user?.email}
              </p>
            )}
          </div>

          <Separator />

          <div className="p-2 space-y-1">
            <Button
              variant="ghost"
              className="w-full justify-start gap-2"
              onClick={() => {
                setOpenProfile(true);
                setIsPopoverOpen(false);
              }}
            >
              <User className="h-4 w-4" />
              View Profile
            </Button>

            <Button
              variant="ghost"
              className="w-full justify-start gap-2"
              onClick={() => {
                setOpenProfile(true);
                setIsPopoverOpen(false);
              }}
            >
              <Camera className="h-4 w-4" />
              Change Photo
            </Button>

            <Button
              variant="ghost"
              className="w-full justify-start gap-2"
              onClick={() => {
                setOpenFeedback(true);
                setIsPopoverOpen(false);
              }}
            >
              <MessageSquare className="h-4 w-4" />
              Leave Feedback
            </Button>

            <Button
              variant="ghost"
              className="w-full justify-start gap-2 text-destructive hover:text-destructive"
              onClick={() => {
                setOpenClearConfirm(true);
                setIsPopoverOpen(false);
              }}
            >
              <Trash2 className="h-4 w-4" />
              Clear All History
            </Button>
          </div>

          <Separator />

          <div className="p-2">
            <Button
              variant="ghost"
              className="w-full justify-start gap-2"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4" />
              Log out
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <ProfileDialog
        open={openProfile}
        onOpenChange={setOpenProfile}
        onSaved={handleProfileSaved}
      />

      <FeedbackDialog open={openFeedback} onOpenChange={setOpenFeedback} />

      <AlertDialog open={openClearConfirm} onOpenChange={setOpenClearConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all chat history?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all your conversations and messages.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearHistory}
              disabled={isClearing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isClearing ? "Clearing..." : "Clear All"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
